import atmosphereVertexSource from './atmosphere.vertex.glsl?raw';
import atmosphereFragmentSource from './atmosphere.fragment.glsl?raw';
import { mat4, vec4 } from 'gl-matrix';

export class AtmosphereLayer {
    constructor(options) {
        this.id = options.id || options;
        this.type = 'custom';
        this.renderingMode = '3d';

        this.map = null;
        this.gl = null;
        this.program = null;
        this.vertexBuffer = null;
        this.indexBuffer = null;
        this.indexCount = 6; // 6 indices for quad (2 triangles)

        // Attribute and uniform locations
        this.aPos = null;
        this.uInvProjMatrix = null;
        this.uGlobePosition = null;
        this.uGlobeRadius = null;
        this.uAtmosphereBlend = null;
        this.uAtmosphereColor = null;
        this.uOuterAtmosphereColor = null;
        this.uAtmosphereRatio = null;
        this.uIntensity = null;
        this.uFalloff = null;

        // Options
        this.atmosphereBlend = options.atmosphereBlend || 1.0;
        this.atmosphereColor = options.atmosphereColor || [1.0, 1.0, 1.0];
        this.outerAtmosphereColor = options.outerAtmosphereColor || [0.0, 0.0, 1.0];
        this.atmosphereRatio = options.atmosphereRatio || 1.1;
        this.intensity = options.intensity || 0.112;
        this.falloff = options.falloff || 3.11;
    }

    onAdd(map, gl) {
        this.map = map;
        this.gl = gl;

        // Create WebGL program
        const vertexShader = this.createShader(gl.VERTEX_SHADER, atmosphereVertexSource);
        const fragmentShader = this.createShader(gl.FRAGMENT_SHADER, atmosphereFragmentSource);

        if (!vertexShader || !fragmentShader) {
            console.error('Failed to create atmosphere shaders');
            return;
        }

        this.program = gl.createProgram();
        if (!this.program) {
            console.error('Failed to create atmosphere program');
            return;
        }

        gl.attachShader(this.program, vertexShader);
        gl.attachShader(this.program, fragmentShader);
        gl.linkProgram(this.program);

        if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
            console.error('Failed to link atmosphere program:', gl.getProgramInfoLog(this.program));
            return;
        }

        // Get attribute and uniform locations
        this.aPos = gl.getAttribLocation(this.program, 'a_pos');
        this.uInvProjMatrix = gl.getUniformLocation(this.program, 'u_inv_proj_matrix');
        this.uGlobePosition = gl.getUniformLocation(this.program, 'u_globe_position');
        this.uGlobeRadius = gl.getUniformLocation(this.program, 'u_globe_radius');
        this.uAtmosphereBlend = gl.getUniformLocation(this.program, 'u_atmosphere_blend');
        this.uAtmosphereColor = gl.getUniformLocation(this.program, 'u_atmosphere_color');
        this.uOuterAtmosphereColor = gl.getUniformLocation(this.program, 'u_outer_atmosphere_color');
        this.uAtmosphereRatio = gl.getUniformLocation(this.program, 'u_atmosphere_ratio');

        // Get new uniform locations
        this.uIntensity = gl.getUniformLocation(this.program, 'u_intensity');
        this.uFalloff = gl.getUniformLocation(this.program, 'u_falloff');

        // Create buffers for a full-screen quad
        this.setupFullScreenQuad();
    }

    render(gl, options) {
        if (!this.program) return;

        const projectionMatrix = options?.projectionMatrix;
        const modelViewProjectionMatrix = options?.modelViewProjectionMatrix;
        if (!projectionMatrix || !modelViewProjectionMatrix) {
            return;
        }

        gl.useProgram(this.program);

        const invProjMatrix = new Float64Array(16);
        mat4.invert(invProjMatrix, projectionMatrix);

        gl.uniformMatrix4fv(this.uInvProjMatrix, false, invProjMatrix);

        const globePosition = this.projectModelPointToEyeSpace([0, 0, 0], modelViewProjectionMatrix, invProjMatrix);
        const globeEdgePosition = this.projectModelPointToEyeSpace([1, 0, 0], modelViewProjectionMatrix, invProjMatrix);

        gl.uniform3fv(this.uGlobePosition, globePosition);

        const globeRadius = Math.hypot(
            globeEdgePosition[0] - globePosition[0],
            globeEdgePosition[1] - globePosition[1],
            globeEdgePosition[2] - globePosition[2]
        );

        // Fade the atmosphere out as the camera approaches the surface 
        const cameraDistance = Math.hypot(globePosition[0], globePosition[1], globePosition[2]);
        const altitudeRatio = cameraDistance / globeRadius - 1;
        const fadeStart = 0.15;
        const fadeEnd = 1.5;
        const t = Math.min(1, Math.max(0, (altitudeRatio - fadeStart) / (fadeEnd - fadeStart)));
        const altitudeFade = t * t * (3 - 2 * t);

        gl.uniform1f(this.uGlobeRadius, globeRadius);
        gl.uniform1f(this.uAtmosphereBlend, this.atmosphereBlend * altitudeFade);
        gl.uniform3fv(this.uAtmosphereColor, this.atmosphereColor);
        gl.uniform3fv(this.uOuterAtmosphereColor, this.outerAtmosphereColor);
        gl.uniform1f(this.uAtmosphereRatio, this.atmosphereRatio);
        gl.uniform1f(this.uIntensity, this.intensity);
        gl.uniform1f(this.uFalloff, this.falloff);

        // Draw the full-screen quad
        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        gl.enableVertexAttribArray(this.aPos);
        gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);

        // Enable necessary WebGL features
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.LEQUAL);
        gl.depthMask(false);

        // Draw the full-screen quad
        gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_SHORT, 0);

        // Clean up
        gl.depthMask(true);
        gl.disable(gl.BLEND);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
        gl.depthFunc(gl.LESS);
        gl.disable(gl.DEPTH_TEST);
    }

    projectModelPointToEyeSpace(modelPoint, modelViewProjectionMatrix, invProjMatrix) {
        const vec = new Float64Array([modelPoint[0], modelPoint[1], modelPoint[2], 1.0]);
        vec4.transformMat4(vec, vec, modelViewProjectionMatrix);
        vec[0] /= vec[3];
        vec[1] /= vec[3];
        vec[2] /= vec[3];
        vec[3] = 1.0;
        vec4.transformMat4(vec, vec, invProjMatrix);
        vec[0] /= vec[3];
        vec[1] /= vec[3];
        vec[2] /= vec[3];
        return [vec[0], vec[1], vec[2]];
    }

    onRemove(map, gl) {
        if (this.program) {
            gl.deleteProgram(this.program);
            this.program = null;
        }

        if (this.vertexBuffer) {
            gl.deleteBuffer(this.vertexBuffer);
            this.vertexBuffer = null;
        }

        if (this.indexBuffer) {
            gl.deleteBuffer(this.indexBuffer);
            this.indexBuffer = null;
        }

        this.map = null;
        this.gl = null;
    }

    createShader(type, source) {
        if (!this.gl) return null;

        const shader = this.gl.createShader(type);
        if (!shader) {
            console.error('Failed to create WebGL shader');
            return null;
        }

        this.gl.shaderSource(shader, source);
        this.gl.compileShader(shader);

        if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
            console.error('Failed to compile atmosphere shader:', this.gl.getShaderInfoLog(shader));
            this.gl.deleteShader(shader);
            return null;
        }

        return shader;
    }

    setupFullScreenQuad() {
        if (!this.gl) return;

        // Create a vertex buffer for a full-screen quad
        const vertices = new Float32Array([
            -1.0,
            -1.0, // bottom left
            1.0,
            -1.0, // bottom right
            1.0,
            1.0, // top right
            -1.0,
            1.0 // top left
        ]);

        this.vertexBuffer = this.gl.createBuffer();
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vertexBuffer);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, vertices, this.gl.STATIC_DRAW);

        // Create an index buffer for drawing the quad as two triangles
        const indices = new Uint16Array([
            0,
            1,
            2, // first triangle
            0,
            2,
            3 // second triangle
        ]);

        this.indexBuffer = this.gl.createBuffer();
        this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
        this.gl.bufferData(this.gl.ELEMENT_ARRAY_BUFFER, indices, this.gl.STATIC_DRAW);

        this.indexCount = indices.length;

        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, null);
        this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, null);
    }

    setBlend(blend) {
        this.atmosphereBlend = Math.max(0, Math.min(1, blend));
        this.map?.triggerRepaint();
    }

    setColor(color) {
        this.atmosphereColor = color;
        this.map?.triggerRepaint();
    }

    setOuterColor(color) {
        this.outerAtmosphereColor = color;
        this.map?.triggerRepaint();
    }

    setRatio(ratio) {
        this.atmosphereRatio = ratio;
        this.map?.triggerRepaint();
    }

    setIntensity(intensity) {
        this.intensity = intensity;
        this.map?.triggerRepaint();
    }

    setFalloff(falloff) {
        this.falloff = falloff;
        this.map?.triggerRepaint();
    }

    setProperties(options) {
        this.atmosphereBlend = options.atmosphereBlend || this.atmosphereBlend;
        this.atmosphereColor = options.atmosphereColor || this.atmosphereColor;
        this.outerAtmosphereColor = options.outerAtmosphereColor || this.outerAtmosphereColor;
        this.atmosphereRatio = options.atmosphereRatio || this.atmosphereRatio;
        this.intensity = options.intensity || this.intensity;
        this.falloff = options.falloff || this.falloff;
        this.map?.triggerRepaint();
    }
}
